import React, { useState } from "react";

import {
	FrontendBatchType,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleInstance,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import { Card } from "@mui/material";
import InstanceInfoCardContent from "../../common/InstanceInfoCardContent";
import CreateOrUpdateSpecificationReadingDrawer from "../../../../common-components/drawers/createOrUpdateSpecificationReadingDrawer";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;

	sampleInstances: Array<FrontendSampleInstance>;
	batchType: FrontendBatchType;
	batchVerifyingSampleId: string | undefined;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	updateSpecification: (spec: FrontendSampleSpecification) => void;
}

export default function BatchInstancesTable(props: Props) {
	const { t: translation } = useTranslation();

	const {
		company,
		setCompany,
		user,
		sampleInstances,
		batchType,
		batchVerifyingSampleId,
		specDirectories,
		updateSpecification,
	} = props;

	const [readingDrawerOpen, setReadingDrawerOpen] = useState<boolean>(false);
	const [selectedSpecification, setSelectedSpecification] = useState<
		FrontendSampleSpecification | undefined
	>();

	const specFileTestCategory: FrontendCompanyTestCategory | undefined =
		selectedSpecification
			? company.companyTestCategories.find(
					(t) => t.testCategoryId === selectedSpecification.testCategoryId,
				)
			: undefined;
	function openSpecReadingDrawer(spec: FrontendSampleSpecification) {
		setSelectedSpecification(spec);
		setReadingDrawerOpen(true);
	}

	const testCategoryNodeParentMap = new Map<
		string,
		Map<string, FrontendCompanyTestHierarchyNode[]>
	>();

	for (const testCategory of company.companyTestCategories) {
		const hierarchyNodeParentMap = new Map<
			string,
			FrontendCompanyTestHierarchyNode[]
		>();

		testCategory.hierarchyNodes.forEach((node) => {
			const parentId = node.parentNodeId || "root";
			if (!hierarchyNodeParentMap.has(parentId))
				hierarchyNodeParentMap.set(parentId, [node]);
			else {
				hierarchyNodeParentMap.get(parentId)!.push(node);
			}
		});
		testCategoryNodeParentMap.set(
			testCategory.testCategoryId,
			hierarchyNodeParentMap,
		);
	}

	const instanceCards: Array<React.ReactNode> = sampleInstances
		? sampleInstances.map(
				(sampleInstance: FrontendSampleInstance, index: number) => {
					const categoryId: string = sampleInstance.sample.testCategoryId;
					const testCategory: FrontendCompanyTestCategory =
						company.companyTestCategories.find((category) => {
							return category.testCategoryId === categoryId;
						}) as FrontendCompanyTestCategory;
					const hierarchyNodeParentMap = testCategoryNodeParentMap.get(
						categoryId,
					) as Map<string, FrontendCompanyTestHierarchyNode[]>;
					return (
						<Card
							className="card-border-radius-14p card-small-padding "
							key={sampleInstance.sampleInstanceId}
						>
							<InstanceInfoCardContent
								company={company}
								instance={sampleInstance}
								batchType={batchType}
								isBatched={false}
								isSelectedAsVerifySample={
									sampleInstance.sampleInstanceId === batchVerifyingSampleId
								}
								selectAsInstanceToVerify={() => {}}
								selectedTestCategory={testCategory}
								hierarchyNodeParentMap={hierarchyNodeParentMap}
								requireDuplicateInstance={batchType.requireDuplicateInstance}
								openSpecReadingDrawer={() => {
									if (sampleInstance.specification) {
										openSpecReadingDrawer(sampleInstance.specification);
									}
								}}
							></InstanceInfoCardContent>
						</Card>
					);
				},
			)
		: [];

	return (
		<>
			<div className={`batch-details-instance-table`}>
				{instanceCards}
				{readingDrawerOpen && selectedSpecification && specFileTestCategory && (
					<CreateOrUpdateSpecificationReadingDrawer
						company={company}
						setCompany={setCompany}
						testCategory={specFileTestCategory}
						// Derive matrix from the sample associated with the spec
						selectedMatrix={
							specFileTestCategory.sampleMatrices.find(
								(m) => m.matrixId === selectedSpecification.matrixId,
							) as FrontendCompanySampleMatrix
						}
						selectedSpecFile={selectedSpecification}
						user={user}
						open={readingDrawerOpen}
						setOpen={setReadingDrawerOpen}
						specDirectories={specDirectories}
						updateSpecification={updateSpecification}
					/>
				)}
			</div>
		</>
	);
}
